/**
 * مستودع عام مبني على IndexedDB، يطبّق سياسات الوصول في كل عملية.
 *
 * لا يمكن تجاوز السياسات من الخدمات: كل قراءة تُنقّى وكل كتابة تُفحص.
 * هذا هو المقابل المحلي لـ RLS، ويُختبر فعليًا في tests/integration.
 */

import type { Filter, FilterOperator, QueryOptions, QueryResult, Repository, TableName, Row } from '../types';
import { AppError } from '@/domain/errors';
import { canReadRow, canWriteRow, type PolicyContext, type RowReadHint } from '../policy';
import { getLocalDb } from './db';

type StoredRow = Record<string, unknown> & { id: string };

export interface RepositoryDeps {
  getPolicyContext: () => PolicyContext;
  /**
   * يضمن أن سياق السياسة محمّل قبل تقييمه. بدونه قد تُقرأ الجداول بسياق فارغ
   * (رفض كل شيء) في أول تحميل للتطبيق قبل اكتمال refresh غير المتزامن.
   */
  ensureReady?: () => Promise<void>;
  /** يوفّر سياق القراءة للجداول المرتبطة (مثل المهمة الأب) */
  resolveHint?: (table: TableName, row: StoredRow) => RowReadHint | undefined | Promise<RowReadHint | undefined>;
  /** تحقق إضافي قبل الإدراج (تحقق من قواعد العمل) */
  beforeInsert?: (table: TableName, row: StoredRow) => Promise<void>;
  beforeUpdate?: (table: TableName, row: StoredRow, patch: Partial<StoredRow>) => Promise<void>;
}

function matchesFilter(row: StoredRow, filter: Filter<StoredRow> | undefined): boolean {
  if (!filter) return true;
  for (const [field, operator] of Object.entries(filter) as Array<[string, FilterOperator]>) {
    if (!operator) continue;
    const value = row[field];
    switch (operator.op) {
      case 'eq':
        if (value !== operator.value) return false;
        break;
      case 'neq':
        if (value === operator.value) return false;
        break;
      case 'in':
        if (!operator.value.includes(value)) return false;
        break;
      case 'isNull':
        if (value !== null && value !== undefined) return false;
        break;
      case 'isNotNull':
        if (value === null || value === undefined) return false;
        break;
      case 'gt':
        if (!(compare(value, operator.value) > 0)) return false;
        break;
      case 'gte':
        if (!(compare(value, operator.value) >= 0)) return false;
        break;
      case 'lt':
        if (!(compare(value, operator.value) < 0)) return false;
        break;
      case 'lte':
        if (!(compare(value, operator.value) <= 0)) return false;
        break;
      case 'contains':
        if (!Array.isArray(value) || !operator.value.some((v) => value.includes(v))) return false;
        break;
      default:
        return false;
    }
  }
  return true;
}

function compare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const aStr = String(a);
  const bStr = String(b);
  return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
}

function applyOrder(rows: StoredRow[], orderBy: QueryOptions<StoredRow>['orderBy']): StoredRow[] {
  if (!orderBy || orderBy.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const order of orderBy) {
      const result = compare(a[order.field], b[order.field]);
      if (result !== 0) return order.direction === 'asc' ? result : -result;
    }
    return 0;
  });
}

export class LocalRepository<T extends TableName> implements Repository<Row<T>> {
  constructor(
    private readonly table: T,
    private readonly deps: RepositoryDeps,
  ) {}

  /** يضمن تحميل سياق السياسة ثم يعيده. */
  private async policy(): Promise<PolicyContext> {
    if (this.deps.ensureReady) await this.deps.ensureReady();
    return this.deps.getPolicyContext();
  }

  private async readAll(): Promise<StoredRow[]> {
    const db = await getLocalDb();
    return (await db.getAll(this.table)) as StoredRow[];
  }

  private assertRowShape(row: unknown): StoredRow {
    if (typeof row !== 'object' || row === null || typeof (row as { id?: unknown }).id !== 'string') {
      throw AppError.validation('السجل غير صالح: معرّف مطلوب', { table: this.table });
    }
    return row as StoredRow;
  }

  async list(options: QueryOptions<Row<T>> = {}): Promise<Row<T>[]> {
    const filtered = await this.filteredRows(options);
    const rows = applyOrder(filtered.rows, options.orderBy as QueryOptions<StoredRow>['orderBy']);
    return this.paginate(rows, options) as unknown as Row<T>[];
  }

  async query(options: QueryOptions<Row<T>> = {}): Promise<QueryResult<Row<T>>> {
    const filtered = await this.filteredRows(options);
    const rows = applyOrder(filtered.rows, options.orderBy as QueryOptions<StoredRow>['orderBy']);
    return {
      rows: this.paginate(rows, options) as unknown as Row<T>[],
      total: options.withCount ? rows.length : null,
    };
  }

  private paginate(rows: StoredRow[], options: QueryOptions<Row<T>>): StoredRow[] {
    const offset = options.offset ?? 0;
    const limit = options.limit;
    if (limit === undefined) return rows.slice(offset);
    return rows.slice(offset, offset + limit);
  }

  private async filteredRows(
    options: QueryOptions<Row<T>>,
  ): Promise<{ rows: StoredRow[]; all: StoredRow[] }> {
    const ctx = await this.policy();
    const all = await this.readAll();
    const rows: StoredRow[] = [];
    for (const row of all) {
      if (!matchesFilter(row, options.filter as Filter<StoredRow> | undefined)) continue;
      const hint = await this.deps.resolveHint?.(this.table, row);
      if (!canReadRow(ctx, this.table, row, hint).allowed) continue;
      rows.push(row);
    }
    return { rows, all };
  }

  async get(id: string): Promise<Row<T> | null> {
    const db = await getLocalDb();
    const row = (await db.get(this.table, id)) as StoredRow | undefined;
    if (!row) return null;
    const ctx = await this.policy();
    const hint = await this.deps.resolveHint?.(this.table, row);
    if (!canReadRow(ctx, this.table, row, hint).allowed) {
      throw AppError.forbidden('لا تملك صلاحية الوصول إلى هذا السجل', { table: this.table });
    }
    return row as unknown as Row<T>;
  }

  async insert(row: Row<T>): Promise<Row<T>> {
    const stored = this.assertRowShape(row);
    const ctx = await this.policy();
    await this.deps.beforeInsert?.(this.table, stored);
    await this.assertWrite(ctx, 'insert', stored);
    const db = await getLocalDb();
    if (this.table === 'tasks') {
      const occurrenceKey = stored['occurrenceKey'] as string | undefined;
      if (occurrenceKey) {
        const existing = (await db.getAllFromIndex('tasks', 'occurrenceKey', occurrenceKey)) as StoredRow[];
        const live = existing.filter((r) => r['deletedAt'] === null || r['deletedAt'] === undefined);
        if (live.length > 0) {
          throw AppError.conflict('يوجد سجل تنفيذ بنفس الموعد لهذا القالب', { occurrenceKey });
        }
      }
    }
    await db.put(this.table, stored);
    return stored as unknown as Row<T>;
  }

  async insertMany(rows: Row<T>[]): Promise<Row<T>[]> {
    const ctx = await this.policy();
    // التحقق الكامل قبل فتح المعاملة، لأن انتظار أي وعد داخل معاملة
    // IndexedDB قد يؤدي إلى إغلاقها تلقائيًا.
    const prepared: StoredRow[] = [];
    for (const row of rows) {
      const stored = this.assertRowShape(row);
      await this.deps.beforeInsert?.(this.table, stored);
      await this.assertWrite(ctx, 'insert', stored);
      prepared.push(stored);
    }
    const db = await getLocalDb();
    const tx = db.transaction(this.table, 'readwrite');
    for (const stored of prepared) await tx.objectStore(this.table).put(stored);
    await tx.done;
    return prepared as unknown as Row<T>[];
  }

  async update(id: string, patch: Partial<Row<T>>): Promise<Row<T>> {
    const db = await getLocalDb();
    const current = (await db.get(this.table, id)) as StoredRow | undefined;
    if (!current) throw AppError.notFound('السجل غير موجود');
    const ctx = await this.policy();
    const patchRecord = patch as Partial<StoredRow>;
    await this.deps.beforeUpdate?.(this.table, current, patchRecord);
    await this.assertWrite(ctx, 'update', current, patchRecord);
    const updated = { ...current, ...patchRecord, id };
    await db.put(this.table, updated);
    return updated as Row<T>;
  }

  async updateIf(id: string, patch: Partial<Row<T>>, expectedVersion: number): Promise<Row<T> | null> {
    const db = await getLocalDb();
    const current = (await db.get(this.table, id)) as StoredRow | undefined;
    if (!current) return null;
    if (Number(current['version'] ?? 0) !== expectedVersion) return null;
    const ctx = await this.policy();
    const patchRecord = patch as Partial<StoredRow>;
    await this.assertWrite(ctx, 'update', current, patchRecord);
    const tx = db.transaction(this.table, 'readwrite');
    const fresh = (await tx.objectStore(this.table).get(id)) as StoredRow | undefined;
    if (!fresh || Number(fresh['version'] ?? 0) !== expectedVersion) {
      await tx.done;
      return null;
    }
    const updated = { ...fresh, ...patchRecord, id, version: expectedVersion + 1 };
    await tx.objectStore(this.table).put(updated);
    await tx.done;
    return updated as Row<T>;
  }

  async updateMany(filter: Filter<Row<T>>, patch: Partial<Row<T>>): Promise<number> {
    const db = await getLocalDb();
    const ctx = await this.policy();
    const patchRecord = patch as Partial<StoredRow>;
    const all = (await db.getAll(this.table)) as StoredRow[];
    const targets: StoredRow[] = [];
    for (const row of all) {
      if (!matchesFilter(row, filter as Filter<StoredRow>)) continue;
      await this.assertWrite(ctx, 'update', row, patchRecord);
      targets.push(row);
    }
    const tx = db.transaction(this.table, 'readwrite');
    for (const row of targets) {
      await tx.objectStore(this.table).put({ ...row, ...patchRecord, id: row.id });
    }
    await tx.done;
    return targets.length;
  }

  async remove(id: string): Promise<void> {
    const db = await getLocalDb();
    const current = (await db.get(this.table, id)) as StoredRow | undefined;
    if (!current) return;
    const ctx = await this.policy();
    await this.assertWrite(ctx, 'delete', current);
    await db.delete(this.table, id);
  }

  async removeMany(filter: Filter<Row<T>>): Promise<number> {
    const db = await getLocalDb();
    const ctx = await this.policy();
    const all = (await db.getAll(this.table)) as StoredRow[];
    const targets: StoredRow[] = [];
    for (const row of all) {
      if (!matchesFilter(row, filter as Filter<StoredRow>)) continue;
      await this.assertWrite(ctx, 'delete', row);
      targets.push(row);
    }
    const tx = db.transaction(this.table, 'readwrite');
    for (const row of targets) await tx.objectStore(this.table).delete(row.id);
    await tx.done;
    return targets.length;
  }

  async count(filter?: Filter<Row<T>>): Promise<number> {
    const { rows } = await this.filteredRows({ filter } as QueryOptions<Row<T>>);
    return rows.length;
  }

  private async assertWrite(
    ctx: PolicyContext,
    operation: 'insert' | 'update' | 'delete',
    row: StoredRow,
    patch?: Partial<StoredRow>,
  ): Promise<void> {
    const hint = await this.deps.resolveHint?.(this.table, { ...row, ...patch });
    const decision = canWriteRow(ctx, this.table, operation, row, patch, hint);
    if (!decision.allowed) {
      throw AppError.forbidden(`العملية مرفوضة: ${decision.reason}`, {
        table: this.table,
        operation,
      });
    }
  }
}
